import React, { useState, useEffect } from 'react';
import { Table, Button, Layout, Space, Input, DatePicker } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import type { RangePickerProps } from 'antd/es/date-picker';
import moment from 'moment';
import api from '../utils/api';
import ReviewDetailModal from './ReviewDetailModal';
import { Content, Header } from 'antd/es/layout/layout';
import TopNav from './TopNav';
import useSetTitle from './GlobalSetTitleHook';
import { useNavigate } from 'react-router-dom';
import { EyeOutlined, SearchOutlined } from '@ant-design/icons';
import { useRef } from 'react';

const { RangePicker } = DatePicker;

interface ReviewRecord {
  id: number;
  reportNumber: string;
  titleCompany?: string;
  address?: string;
  reportDate?: string;
  reviewDate?: string;
  reviewStatus?: string;
  created_by?: { last_name?: string; username?: string; id?: number };
}

const ReviewHistory: React.FC = () => {
  useSetTitle();
  const navigate = useNavigate();

  const [records, setRecords] = useState<ReviewRecord[]>([]);
  const [selectedRecord, setSelectedRecord] = useState<ReviewRecord | null>(null);
  const [modalVisible, setModalVisible] = useState(false);

  const [page, setPage] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(10);
  const [total, setTotal] = useState<number>(0);

  // 全量搜索字段 filters
  const [filters, setFilters] = useState<Record<string, any>>({});

  // ====================================
  // fetchReviews：通用请求函数
  // ====================================
  const fetchReviews = async (p?: number, ps?: number, extraFilters: Record<string, any> = {}) => {
    try {
      const pageNum = p ?? page;
      const pageSizeNum = ps ?? pageSize;
      const mergedFilters = { ...filters, ...extraFilters };

      const params = new URLSearchParams({
        page: String(pageNum),
        size: String(pageSizeNum),
      });

      Object.entries(mergedFilters).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          params.append(key, String(value));
        }
      });

      const response = await api.get(`/api/reviews/?${params.toString()}`);
      setRecords(response.data?.data ?? []);
      setTotal(response.data?.totalCount ?? 0);
      setPage(pageNum);
      setPageSize(pageSizeNum);
    } catch (error) {
      console.error('Failed to fetch review history:', error);
    }
  };

  const debounce = (fn: Function, delay: number) => {
    let timer: NodeJS.Timeout;
    return (...args: any[]) => {
      if (timer) clearTimeout(timer);
      timer = setTimeout(() => fn(...args), delay);
    };
  };

  useEffect(() => {
    fetchReviews(page, pageSize, filters);
  }, [filters, page, pageSize]);

  const handlePageChange = (p: number, ps?: number) => {
    const size = ps ?? pageSize;
    fetchReviews(p, size);
  };

  // 普通搜索
  const handleSearch = (field: string, value: string) => {
    const newFilters = { ...filters, [field]: value };
    setFilters(newFilters);
    fetchReviews(1, pageSize, newFilters);
  };

  // 重置搜索字段
  const handleReset = (field: string) => {
    // 先清掉组件状态 filters
    const newFilters = { ...filters };
    delete newFilters[field];
    setFilters(newFilters);

    // 发请求
    fetchReviews(1, pageSize, newFilters);
  };

  // ====================================
  // 表头搜索框（文本）
  // ====================================

  const getColumnSearchProps = (dataIndex: string) => ({
    filterDropdown: (props: any) => {
      const { setSelectedKeys, selectedKeys, confirm } = props;
      const currentValue = (selectedKeys as string[] | undefined)?.[0] ?? '';

      return (
        <div style={{ padding: 8 }}>
          <Input
            placeholder="搜索关键字"
            value={currentValue}
            onChange={(e) => setSelectedKeys?.(e.target.value ? [e.target.value] : [])}
            onPressEnter={() => {
              const val = (selectedKeys as string[] | undefined)?.[0] ?? '';
              handleSearch(dataIndex, val);
              confirm();
            }}
            style={{ width: 188, marginBottom: 8, display: 'block' }}
          />
          <Space>
            <Button
              type="primary"
              size="small"
              icon={<SearchOutlined />}
              onClick={() => {
                const val = (selectedKeys as string[] | undefined)?.[0] ?? '';
                handleSearch(dataIndex, val);
                confirm();
              }}
            >
              搜索
            </Button>
            <Button
              size="small"
              onClick={() => {
                // 清空输入框
                setSelectedKeys?.([]);
                // 清空对应 filter
                const newFilters = { ...filters };
                delete newFilters[dataIndex];
                setFilters(newFilters);
                confirm();
              }}
            >
              重置
            </Button>
          </Space>
        </div>
      );
    },
    filterIcon: (filtered: boolean) => (
      <SearchOutlined style={{ color: filtered ? '#1677ff' : undefined }} />
    ),
  });

  // ====================================
  // 日期范围搜索
  // ====================================
  const getColumnDateRangeProps = (startKey: string, endKey: string) => ({
    filterDropdown: (props: any) => {
      const { confirm, clearFilters } = props;
      let valueRange: [moment.Moment, moment.Moment] | null = null;

      const handleChange: RangePickerProps['onChange'] = (dates) => {
        valueRange = dates as [moment.Moment, moment.Moment] | null;
      };

      const handleOk = () => {
        if (valueRange && valueRange[0] && valueRange[1]) {
          handleSearch(startKey, valueRange[0].format('YYYY-MM-DD'));
          handleSearch(endKey, valueRange[1].format('YYYY-MM-DD'));
        }
        confirm();
      };

      return (
        <div style={{ padding: 8 }}>
          <RangePicker onChange={handleChange} style={{ marginBottom: 8, display: 'block' }} />
          <Space>
            <Button type="primary" size="small" onClick={handleOk}>
              搜索
            </Button>
            <Button
              size="small"
              onClick={() => {
                clearFilters?.();
                handleReset(startKey);
                handleReset(endKey);
                confirm();
              }}
            >
              重置
            </Button>
          </Space>
        </div>
      );
    },
    filterIcon: (filtered: boolean) => (
      <SearchOutlined style={{ color: filtered ? '#1677ff' : undefined }} />
    ),
  });

  // ====================================
  // 表格列定义
  // ====================================
  const columns: ColumnsType<ReviewRecord> = [
    {
      title: '报告编号',
      dataIndex: 'reportNumber',
      key: 'reportNumber',
      render: (_, item) => item?.reportNumber ?? '',
      ...getColumnSearchProps('reportNumber'),
    },
    {
      title: '报告抬头公司',
      dataIndex: 'titleCompany',
      key: 'titleCompany',
      render: (_, item) => item?.titleCompany ?? '',
      ...getColumnSearchProps('titleCompany'),
    },
    {
      title: '地址',
      dataIndex: 'address',
      key: 'address',
      render: (_, item) => item?.address ?? '',
      ...getColumnSearchProps('address'),
    },
    {
      title: '报告日期',
      dataIndex: 'reportDate',
      key: 'reportDate',
      render: (_, item) => item?.reportDate ?? '',
      ...getColumnDateRangeProps('reportDateStart', 'reportDateEnd'),
    },
    {
      title: '审核日期',
      dataIndex: 'reviewDate',
      key: 'reviewDate',
      render: (_, item) => item?.reviewDate ?? '',
      ...getColumnDateRangeProps('reviewDateStart', 'reviewDateEnd'),
    },
    {
      title: '审核状态',
      dataIndex: 'reviewStatus',
      key: 'reviewStatus',
      render: (_, item) => item?.reviewStatus ?? '',
      ...getColumnSearchProps('reviewStatus'),
    },
    {
      title: '审核人',
      dataIndex: 'userName',
      key: 'userName',
      render: (_, item) => item?.created_by?.last_name ?? '',
      ...getColumnSearchProps('createdBy'),
    },
    {
      title: '操作',
      key: 'action',
      render: (item: ReviewRecord) => (
        <Button
          type="link"
          icon={<EyeOutlined />}
          onClick={() => {
            setSelectedRecord(item);
            setModalVisible(true);
            navigate(`/audit-detail?id=${item?.id}`, { state: { record: item } });
          }}
        >
          查看
        </Button>
      ),
    },
  ];

  return (
    <Layout className="layout">
      <Header style={{ background: '#f0f2f5', padding: 0 }}>
        <TopNav />
      </Header>
      <Content>
        <div style={{ padding: '20px' }}>
          <h2>审核历史</h2>
          <Table
            columns={columns}
            dataSource={records}
            rowKey="id"
            pagination={{
              pageSize,
              current: page,
              total,
              showSizeChanger: true,
              onChange: handlePageChange,
            }}
          />

          {selectedRecord && (
            <ReviewDetailModal
              visible={modalVisible}
              record={selectedRecord}
              onClose={() => setModalVisible(false)}
            />
          )}
        </div>
      </Content>
    </Layout>
  );
};

export default ReviewHistory;
